import { fetchStationList, fetchStoreAreaList, getStaionInfoByCode } from '@/services/api/storeStation'
import type { FetchStationAreaListParams, StationPageReq } from '@/services/api/storeStation/types'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { OneMessage } from '@yy/one-ui'
import { pick, pickBy } from 'lodash'
import type { AppDispatch, AppGetState } from '..'
import type * as Types from './types'
import type { Warehouse } from '@/services/api'

const initialState = {
  curExpressInfo: {},
  storeData: {},
  expressList: [],
  warehouseInfo: {},
} as Types.IInitialState

const expressSlice = createSlice({
  name: 'express',
  initialState: initialState,
  reducers: {
    /** 当前的服务产品信息 */
    setCurExpressInfo(state, action) {
      return {
        ...state,
        curExpressInfo: {
          ...state.curExpressInfo,
          ...action.payload,
        },
      }
    },
    /** 更新门店信息 */
    updateStoreData(state, action: PayloadAction<Partial<Types.IInitialState['storeData']>>) {
      return {
        ...state,
        storeData: {
          ...state.storeData,
          ...action.payload,
        },
      }
    },
    /** 重置门店信息 */
    resetStationState(state) {
      const stationOriginState = pick(initialState.storeData, ['stationQueryKey', 'stationList', 'stationListHasNext', 'stationListPageNum'])
      return { ...state, ...stationOriginState }
    },

    setExpressList(state, action) {
      return {
        ...state,
        expressList: action.payload,
      }
    },
    setWarehouseInfo(state, action: PayloadAction<Warehouse>) {
      return {
        ...state,
        warehouseInfo: action.payload,
      }
    },
  },
})

export const { setCurExpressInfo, updateStoreData, resetStationState, setExpressList, setWarehouseInfo } = expressSlice.actions

export default expressSlice.reducer

/** 获取店配地址省/市/区 */
export const fetchRecipientAddressStoreArea = (params: FetchStationAreaListParams) => async (dispatch: AppDispatch) => {
  try {
    const data = await fetchStoreAreaList(params)
    dispatch(updateStoreData({ storeAreaList: data }))
  } catch (error) {}
}

/** 获取门店列表分页 | 收货地址 - 店配 */
export const fetchRecipientAddressStoreStationPagination = (params: StationPageReq) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { express } = getState()

  const { list: stationList, hasNext: stationListHasNext, pageNum: stationListPageNum } = await fetchStationList(params)

  if ((params as any).pageNum === 1) {
    dispatch(
      updateStoreData({
        stationList,
        stationListHasNext,
        stationListPageNum,
      })
    )

    return stationList
  }

  const { stationList: originStationList } = express.storeData
  dispatch(
    updateStoreData({
      stationList: [].concat(originStationList, stationList),
      stationListHasNext,
      stationListPageNum,
    })
  )
}

export const queryRecipientAddressStoreStation: Types.IQueryStoreStation =
  (params: Types.IQueryKeyword | Types.IQueryAddress, formValues: any) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const { express } = getState()
    const { pageNum = 1, countryCode = formValues.consigneeCountry, stationType = formValues.arrival_station_type } = params
    const { stationCompanyCode: companyCode } = express.curExpressInfo
    if ('queryKey' in params) {
      const keywords = params.queryKey
      dispatch(updateStoreData({ stationQueryKey: keywords }))
      await dispatch(fetchRecipientAddressStoreStationPagination({ countryCode, companyCode, stationType, keywords, pageNum }))
    } else {
      const [province, city, district] = formValues.storeAreaObj || []
      const optionalPayload = {
        province: params.province || province,
        city: params.city || city,
        district: params.district || district,
      }

      const paylad = pickBy<typeof optionalPayload>(optionalPayload, Boolean)
      await dispatch(fetchRecipientAddressStoreStationPagination({ countryCode, companyCode, stationType, pageNum, ...paylad }))
    }
  }

/** 搜索门店列表分页 | 收货地址 - 店配 */
export const queryStationPagination = (formValues: any) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { express } = getState()

  const { stationQueryKey, stationListPageNum } = express.storeData

  const params: Partial<Types.IQueryKeyword> = {
    pageNum: stationListPageNum + 1,
  }

  if (stationQueryKey) {
    params.queryKey = stationQueryKey
  }

  await dispatch(queryRecipientAddressStoreStation(params, formValues))
}
/** 获取物流列表 */
export const getExpressList = (warehouseId) => async (dispatch: AppDispatch, getState: AppGetState) => {
  if (!warehouseId) return {}
  const { common } = getState()
  const warehouseInfo = common.warehouseInfoList?.find((item) => item.warehouseId === warehouseId)
  /**
   * 设置服务产品列表以及仓库信息
   */
  dispatch(setExpressList(warehouseInfo?.warehouseExpresses))
  dispatch(setWarehouseInfo(warehouseInfo))
  return {
    warehouseExpresses: warehouseInfo?.warehouseExpresses,
    warehouseInfo,
  }
}
/** 请求lms渠道的补充信息信息 */
export const fetchExpressChannelInfo =
  ({ expressType, companyCode }) =>
  async (dispatch: AppDispatch) => {
    try {
      if (!expressType) return
      const { data, code, message } = await getStaionInfoByCode({ omsExpressCode: expressType, companyCode })

      if (code === 'SUCCESS') {
        dispatch(setCurExpressInfo(data))
        return data
      } else {
        message && OneMessage.error(message)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }
